/**
 * As seis entidades de cadastro, descritas como dado em vez de seis telas.
 *
 * Foi assim que ficou combinado: *"tem que ser um componente à parte, pra que
 * ele possa ser utilizado dentro dos outros sistemas"*. O painel de cadastro é a
 * mesma peça na aba de Cadastros, dentro do diagrama e a partir do "+" do
 * lançamento de pinout — o que muda entre eles é só o esquema abaixo.
 *
 * Cada esquema diz: a rota, os campos do formulário, as colunas da lista, e o
 * que fazer quando a exclusão bate num vínculo.
 */

import { ORDEM_TIPOS_CIRCUITO, TIPOS_COMPONENTE } from "../pinout/tipos"

export type TipoDeCampo = "texto" | "numero" | "lista" | "area"

export type Campo = {
  nome: string
  rotulo: string
  tipo: TipoDeCampo
  /** Identificador vai em monoespaçado — regra 8. */
  mono?: boolean
  obrigatorio?: boolean
  dica?: string
  /**
   * Para `lista`: o mapa de valor → rótulo. Quando a ordem importa, vai como
   * lista de pares — um objeto de chave numérica sempre sai em ordem numérica.
   */
  opcoes?: Record<number, string> | ReadonlyArray<readonly [number, string]>
  /** Largura relativa dentro da linha do formulário. 1 = metade, 2 = inteira. */
  largura?: 1 | 2
  ajuda?: string
}

export type Esquema = {
  chave: EntidadeCadastro
  rotulo: string
  /** Singular, para os títulos: "Novo componente", "Excluir conector". */
  singular: string
  /**
   * Para "este/esta" nas frases. Não dá para derivar da palavra: "sistema"
   * termina em -a e é masculino, "restrição" em -ão e é feminino. Aqui os
   * femininos são cor e restrição.
   */
  feminino?: boolean
  artigo: "o" | "a"
  rota: string
  /** Campo que identifica a linha na lista e no título do painel. */
  titulo: string
  campos: Campo[]
  /** Colunas extras da lista, além de descrição e uso. */
  colunas: { nome: string; rotulo: string; largura: number; mono?: boolean }[]
  /** Aparece no painel quando a entidade tem alguma sutileza que morde. */
  nota?: string
}

export type EntidadeCadastro =
  | "componentes" | "conectores" | "circuitos" | "cores" | "chicotes" | "restricoes"

export const ESQUEMAS: Record<EntidadeCadastro, Esquema> = {
  componentes: {
    chave: "componentes",
    rotulo: "Componentes",
    singular: "componente",
    artigo: "o",
    rota: "api/componentes",
    titulo: "codigo",
    campos: [
      { nome: "codigo", rotulo: "Código", tipo: "texto", mono: true, obrigatorio: true, dica: "M01, E004", largura: 1 },
      { nome: "descricao", rotulo: "Descrição", tipo: "texto", obrigatorio: true, dica: "Módulo de injeção", largura: 2 },
      { nome: "apelido", rotulo: "Apelido", tipo: "texto", largura: 1 },
      { nome: "localizacao", rotulo: "Localização", tipo: "texto", largura: 1, dica: "onde fica no veículo" },
      {
        nome: "tipo", rotulo: "Tipo", tipo: "lista", opcoes: TIPOS_COMPONENTE, largura: 2,
        ajuda: "Aterramento e junção mudam como o componente entra no diagrama gerado.",
      },
    ],
    colunas: [
      { nome: "codigo", rotulo: "Código", largura: 100, mono: true },
      { nome: "localizacao", rotulo: "Localização", largura: 160 },
    ],
  },

  conectores: {
    chave: "conectores",
    rotulo: "Conectores",
    singular: "conector",
    artigo: "o",
    rota: "api/conectores",
    titulo: "descricao",
    campos: [
      { nome: "descricao", rotulo: "Descrição", tipo: "texto", mono: true, obrigatorio: true, dica: "C413", largura: 1 },
      { nome: "apelido", rotulo: "Apelido", tipo: "texto", largura: 1 },
      { nome: "qtdPinos", rotulo: "Quantidade de pinos", tipo: "numero", largura: 1 },
      { nome: "prefixo", rotulo: "Prefixo do pino", tipo: "texto", mono: true, largura: 1, dica: "A, B…" },
      { nome: "menorPino", rotulo: "Menor pino", tipo: "texto", mono: true, largura: 1 },
      { nome: "maiorPino", rotulo: "Maior pino", tipo: "texto", mono: true, largura: 1 },
      { nome: "tipoConector", rotulo: "Tipo do conector", tipo: "texto", largura: 1 },
      { nome: "tipoContagem", rotulo: "Tipo de contagem", tipo: "texto", largura: 1 },
      { nome: "obs", rotulo: "Observação", tipo: "area", largura: 2 },
    ],
    colunas: [{ nome: "qtdPinos", rotulo: "Pinos", largura: 70, mono: true }],
    nota: "O conector pertence a um componente, e o nome dele não é único: dois componentes podem ter um “C413”. Por isso o componente aparece na primeira coluna, sempre. O filtro acima encurta a lista quando se sabe de quem se procura.",
  },

  circuitos: {
    chave: "circuitos",
    rotulo: "Circuitos",
    singular: "circuito",
    artigo: "o",
    rota: "api/circuitos",
    titulo: "descricao",
    campos: [
      {
        nome: "descricao", rotulo: "Descrição", tipo: "texto", mono: true, obrigatorio: true,
        dica: "1BRPT1.E", largura: 1,
        ajuda: "O ponto separa o circuito do trecho: 1BRPT1 é o circuito, .E é o trecho. Trechos do mesmo fio cruzam chicotes diferentes.",
      },
      { nome: "apelido", rotulo: "Apelido", tipo: "texto", largura: 1 },
      { nome: "tipo", rotulo: "Tipo", tipo: "lista", opcoes: ORDEM_TIPOS_CIRCUITO, largura: 2 },
    ],
    colunas: [],
  },

  cores: {
    chave: "cores",
    rotulo: "Cores",
    singular: "cor",
    feminino: true,
    artigo: "a",
    rota: "api/cores-projeto",
    titulo: "descricao",
    campos: [
      {
        nome: "descricao", rotulo: "Sigla", tipo: "texto", mono: true, obrigatorio: true,
        dica: "BR/PT, AM, VD/VM", largura: 2,
        ajuda: "A sigla é o dado. Não se guarda amostra da cor, e fica assim — mexer nisso mexeria no banco.",
      },
    ],
    colunas: [],
  },

  chicotes: {
    chave: "chicotes",
    rotulo: "Chicotes",
    singular: "chicote",
    artigo: "o",
    rota: "api/chicotes",
    titulo: "descricao",
    campos: [
      { nome: "descricao", rotulo: "Descrição", tipo: "texto", obrigatorio: true, dica: "Carroceria traseira", largura: 2 },
    ],
    colunas: [],
  },

  restricoes: {
    chave: "restricoes",
    rotulo: "Restrições",
    singular: "restrição",
    feminino: true,
    artigo: "a",
    rota: "api/restricoes",
    titulo: "descricao",
    campos: [
      {
        nome: "descricao", rotulo: "Descrição", tipo: "texto", obrigatorio: true,
        dica: "Teto solar, Dualogic", largura: 2,
        ajuda: "A restrição limita o pinout a certos modelos (RN-01). Filtrar a grade por modelo passa a esconder o que não se aplica.",
      },
    ],
    colunas: [],
  },
}

export const ABAS: EntidadeCadastro[] =
  ["componentes", "conectores", "circuitos", "cores", "chicotes", "restricoes"]

/** Um registro em branco, para o formulário de inclusão. */
export function vazio(e: Esquema): Record<string, unknown> {
  const r: Record<string, unknown> = {}
  for (const c of e.campos) r[c.nome] = c.tipo === "numero" || c.tipo === "lista" ? 0 : ""
  return r
}

/** "este" ou "esta", conforme o cadastro. Ver o campo `feminino` no tipo. */
export function esteEsta(e: { feminino?: boolean }) {
  return e.feminino ? "esta" : "este"
}
